import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Container } from '@/components/legacy-ui';
import { SUBSCRIPTION_TIERS } from '@/sections/settings/subscription/constant';
import { SubscriptionTier } from '@/types';
import { cn } from '@/utils/classname';

interface FeatureRowProps {
  featureName: string;
  isEnabled: boolean;
  removeTextHighlight?: boolean;
}

export function FeatureRow({
  featureName,
  isEnabled,
  removeTextHighlight,
}: FeatureRowProps) {
  const featureColor = isEnabled
    ? 'text-chariot-brand-primary'
    : 'text-chariot-text-disabled';
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="flex-shrink-0">
        {isEnabled ? (
          <CheckIcon className={cn('size-4', featureColor)} />
        ) : (
          <XMarkIcon className={cn('size-4', featureColor)} />
        )}
      </div>
      <span
        className={cn(
          'text-sm',
          !removeTextHighlight && featureColor,
          removeTextHighlight && !isEnabled && featureColor
        )}
      >
        {featureName}
      </span>
    </div>
  );
}

interface FeatureCategoryProps {
  categoryName: string;
  features: Record<string, boolean>;
  removeTextHighlight?: boolean;
}

export function FeatureCategory({
  categoryName,
  features,
  removeTextHighlight,
}: FeatureCategoryProps) {
  return (
    <div className="mb-4">
      <h4 className="text-chariot-text-primary mb-3 text-sm font-bold tracking-wide uppercase">
        {categoryName}
      </h4>
      <div className="flex flex-col gap-1">
        {Object.entries(features).map(([featureName, isEnabled]) => (
          <FeatureRow
            key={featureName}
            featureName={featureName}
            isEnabled={isEnabled}
            removeTextHighlight={removeTextHighlight}
          />
        ))}
      </div>
    </div>
  );
}

interface TierCardProps {
  tier: SubscriptionTier;
  isSelected: boolean;
  onClick?: (tier: string) => void;
}

function TierCard({ tier, isSelected, onClick }: TierCardProps) {
  return (
    <Container
      isSelected={isSelected}
      className={cn(
        'relative flex-1 p-6 focus:outline-hidden',
        onClick && 'cursor-pointer'
      )}
      onClick={onClick ? () => onClick(tier.id) : undefined}
      data-testid={`tier-card-${tier.id}`}
    >
      {isSelected && (
        <div className="bg-chariot-brand-primary absolute -top-3 left-1/2 -translate-x-1/2 transform rounded-full px-4 py-2 text-xs font-bold text-white">
          YOUR PLAN
        </div>
      )}
      {/* Tier name */}
      <div className="border-chariot-stroke mb-6 border-b pb-4 text-center">
        <h3 className="text-chariot-text-primary text-lg font-semibold">
          {tier.displayName}
        </h3>
      </div>

      {/* Features */}
      <div className="space-y-2">
        {Object.entries(tier.features).map(([categoryName, features]) => (
          <FeatureCategory
            key={categoryName}
            categoryName={categoryName}
            features={features}
          />
        ))}
      </div>
    </Container>
  );
}

export function SubscriptionTiers(stateProps: {
  selectedTier: string;
  setSelectedTier?: (tier: string) => void;
}) {
  return (
    <div
      className="flex w-full flex-wrap gap-6"
      data-testid="subscription-tiers"
      data-tutorial="subscription-tiers"
    >
      {SUBSCRIPTION_TIERS.map(tier => (
        <TierCard
          key={tier.id}
          tier={tier}
          isSelected={stateProps.selectedTier === tier.id}
          onClick={stateProps.setSelectedTier}
        />
      ))}
    </div>
  );
}
